/**
 * JudgePrintPage - Printable judge papers (tuomarilaput).
 *
 * Opens in new tab, displays A4 landscape pages ready for printing.
 * Each block/court gets one page with schedule and score entry columns.
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { printApi, mediaApi, contactsApi } from '@/api/client';
import type { JudgePrintData, PrintAssets, PrintContacts } from '@/types';

import './print.css';

interface JudgePaperProps {
  block: JudgePrintData;
  assets: PrintAssets | undefined;
  contacts: PrintContacts | undefined;
}

function JudgePaper({ block, assets, contacts }: JudgePaperProps) {
  const leagueNames: Record<string, string> = {
    yle: 'Yleinen',
    pro: 'PRO',
    frm: 'Firma',
  };

  // Get logos from assets, fallback to empty array
  const clubLogos = assets?.club_logos || [];
  const kyykkaLogos = assets?.kyykka_logos || [];

  return (
    <div className="print-page a4-landscape">
      {/* Header */}
      <div className="print-header">
        <div className="print-header-left">
          <span className="print-league-badge">{block.paper_id_display}</span>
          <h1 className="print-title">Tuomarilappu</h1>
          <p className="print-subtitle">
            {leagueNames[block.league_id] || block.league_id}-sarja, {block.round}. kierros, 
            kenttä {block.court_id || '?'}, klo {block.schedule_start} - {block.schedule_end}
          </p>
          <p className="print-info">
            Jos TUHO:ssa on häiriö; täytä lappu ja palauta se heti lapussa olevien pelien päätyttyä päätuomarille.
          </p>
          <p className="print-info">
            Lapun oikeassa alakulmassa linkki livetuloksiin. Linkit: https://kyykka.fi/tuomarit
          </p>
        </div>

        <div className="print-header-right">
          {/* Kyykkä logos from media assets */}
          {kyykkaLogos.map((logo) => (
            <img 
              key={logo.id}
              className="print-logo" 
              src={logo.file_url} 
              alt={logo.name}
            />
          ))}
          {/* Club logos from media assets */}
          {clubLogos.map((logo) => (
            <img 
              key={logo.id}
              className="print-logo" 
              src={logo.file_url} 
              alt={logo.name}
            />
          ))}
        </div>
      </div>

      {/* Schedule table with score columns */}
      <table className="print-table">
        <thead>
          <tr>
            <th>Klo</th>
            <th>Joukkue 1</th>
            <th>Joukkue 2</th>
            <th>1. pääty</th>
            <th>2. pääty</th>
            <th>Lopputulos</th>
            <th>Voittaja</th>
          </tr>
        </thead>
        <tbody>
          {block.games.map((game) => (
            <tr key={game.id}>
              <td className="time">{game.schedule_start}</td>
              <td className="team">{game.team1_name}</td>
              <td className="team">{game.team2_name}</td>
              <td className="score">
                {game.team1_1st_score ?? '___'} / {game.team2_1st_score ?? '___'}
              </td>
              <td className="score">
                {game.team1_2nd_score ?? '___'} / {game.team2_2nd_score ?? '___'}
              </td>
              <td className="score">
                {game.team1_score ?? '___'} / {game.team2_score ?? '___'}
              </td>
              <td className="winner">
                {game.winner_name || '_________________'}
                {game.game_ot && game.team1_ot_score != null && (
                  <span> (OT {game.team1_ot_score}/{game.team2_ot_score})</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pathway section */}
      {block.pathway && block.pathway.length > 0 && (
        <div className="print-pathway">
          <h3>Lohkovoittajan jatkopelit:</h3>
          <table className="print-pathway-table">
            <tbody>
              <tr>
                <td className="time-label">Klo</td>
                {block.pathway.map((step) => (
                  <td key={`time-${step.paper_id}`} className="playoff">
                    {step.schedule_start} - {step.schedule_end}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="field-label">Kenttä</td>
                {block.pathway.map((step) => (
                  <td key={`field-${step.paper_id}`} className="playoff">
                    {step.court_id}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Footer - 3 sections */}
      <div className="print-footer-3col">
        {/* Left: Manual fields */}
        <div className="print-footer-left">
          <p className="print-manual-field-large"><strong>Tuomari:</strong> __________________</p>
          <p className="print-manual-field-large"><strong>Lohko päättyi klo:</strong> __________________</p>
        </div>

        {/* Middle: Contacts */}
        <div className="print-footer-middle">
          <p><strong>Päätuomarit</strong></p>
          <ul>
            {block.league_id === 'frm' ? (
              // Firma: Show firma päätuomarit
              contacts?.paatuomari_firma.map((c) => (
                <li key={c.id}>{c.name} <strong>{c.phone}</strong></li>
              ))
            ) : block.league_id === 'pro' ? (
              // PRO: Show PRO päätuomarit (primary, underlined) + yleinen
              <>
                {contacts?.paatuomari_pro.map((c) => (
                  <li key={c.id}><u>{c.name}</u> <strong>{c.phone}</strong></li>
                ))}
                {contacts?.paatuomari_yle.map((c) => (
                  <li key={c.id}>{c.name} <strong>{c.phone}</strong></li>
                ))}
              </>
            ) : (
              // Yleinen: Show yleinen päätuomarit
              contacts?.paatuomari_yle.map((c) => (
                <li key={c.id}>{c.name} <strong>{c.phone}</strong></li>
              ))
            )}
          </ul>
          <p><strong>Tulospalvelu</strong></p>
          <ul>
            {contacts?.tulospalvelu.map((c) => (
              <li key={c.id}>{c.name} <strong>{c.phone}</strong></li>
            ))}
          </ul>
        </div>

        {/* Right: QR code */}
        <div className="print-footer-qr">
          <p className="print-qr-label">https://app.kyykka.fi/block/{block.paper_id}</p>
          <img className="print-qr" src={block.qr_code} alt="QR Code" />
        </div>
      </div>
    </div>
  );
}

export function JudgePrintPage() {
  const [searchParams] = useSearchParams();
  const league = searchParams.get('league') || undefined;
  const paperId = searchParams.get('id');

  // Set print mode class on body
  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => {
      document.body.classList.remove('print-mode');
    };
  }, []);

  // Fetch print data
  const { data: blocks, isLoading: blocksLoading, error } = useQuery({
    queryKey: ['print-judges', league, paperId],
    queryFn: async () => {
      if (paperId) {
        const block = await printApi.getJudge(parseInt(paperId, 10));
        return [block];
      }
      return printApi.getJudges(league);
    },
  });

  // Fetch media assets
  const { data: assets, isLoading: assetsLoading } = useQuery({
    queryKey: ['print-assets'],
    queryFn: () => mediaApi.getPrintAssets(),
  });

  // Fetch contacts
  const { data: contacts, isLoading: contactsLoading } = useQuery({
    queryKey: ['print-contacts'],
    queryFn: () => contactsApi.getPrintContacts(),
  });

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    window.close();
  };

  const isLoading = blocksLoading || assetsLoading || contactsLoading;

  if (isLoading) {
    return (
      <div className="print-controls">
        <span>Ladataan...</span>
      </div>
    );
  }

  if (error || !blocks) {
    return (
      <div className="print-controls">
        <span>Virhe ladattaessa tietoja</span>
        <button className="secondary" onClick={handleClose}>Sulje</button>
      </div>
    );
  }

  return (
    <>
      {/* Print controls */}
      <div className="print-controls">
        <button onClick={handlePrint}>🖨️ Tulosta ({blocks.length} sivua)</button>
        <button className="secondary" onClick={handleClose}>Sulje</button>
      </div>

      {/* Judge papers */}
      {blocks.map((block) => (
        <JudgePaper key={block.paper_id} block={block} assets={assets} contacts={contacts} />
      ))}
    </>
  );
}
